import { Box } from '@mui/material';

interface ScreenInfo {
  label: string;
  width: number;
  height: number;
  left: number;
  top: number;
  isPrimary: boolean;
}

interface ScreenMapProps {
  screens: ScreenInfo[];
  selectedLabel: string | null;
  roles: Record<string, string>;
  onSelect: (label: string) => void;
}

/**
 * Visual representation of connected monitors — renders proportionally-sized
 * rectangles based on actual pixel dimensions and relative positions.
 */
export function ScreenMap({ screens, selectedLabel, roles, onSelect }: ScreenMapProps) {
  if (screens.length === 0) return null;

  // Calculate bounding box of all screens
  const minLeft = Math.min(...screens.map((s) => s.left));
  const minTop = Math.min(...screens.map((s) => s.top));
  const maxRight = Math.max(...screens.map((s) => s.left + s.width));
  const maxBottom = Math.max(...screens.map((s) => s.top + s.height));
  const totalW = maxRight - minLeft;
  const totalH = maxBottom - minTop;

  // Scale to fit within ~460px width, maintaining aspect ratio
  const containerWidth = 460;
  const scale = Math.min(containerWidth / totalW, 250 / totalH);
  const containerHeight = totalH * scale;

  return (
    <Box
      sx={{
        position: 'relative',
        width: containerWidth,
        height: containerHeight,
        mx: 'auto',
        my: 2,
      }}
    >
      {screens.map((screen, idx) => {
        const x = (screen.left - minLeft) * scale;
        const y = (screen.top - minTop) * scale;
        const w = screen.width * scale;
        const h = screen.height * scale;
        const isSelected = screen.label === selectedLabel;
        const roleLabel = roles[screen.label] ?? 'unassigned';

        return (
          <Box
            key={screen.label || idx}
            onClick={() => onSelect(screen.label)}
            sx={{
              position: 'absolute',
              left: x,
              top: y,
              width: w,
              height: h,
              border: isSelected ? '2px solid #1976d2' : '2px solid #666',
              borderRadius: 1,
              backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0,0,0,0.03)',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'border-color 0.15s, background-color 0.15s',
              '&:hover': {
                borderColor: '#1976d2',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            <Box sx={{ fontSize: 13, fontWeight: 600 }}>
              {screen.label || `Monitor ${idx + 1}`}
            </Box>
            <Box sx={{ fontSize: 11, color: 'text.secondary' }}>
              {screen.width}×{screen.height}
            </Box>
            <Box
              sx={{
                mt: 0.5,
                fontSize: 11,
                px: 1,
                py: 0.25,
                borderRadius: 0.5,
                backgroundColor: isSelected ? '#1976d2' : '#888',
                color: '#fff',
              }}
            >
              {formatRole(roleLabel)}
            </Box>
            {screen.isPrimary && (
              <Box sx={{ fontSize: 10, color: 'text.secondary', mt: 0.25 }}>
                (Primary)
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function formatRole(role: string): string {
  return role
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
